import { useMassActionsContext } from '@components/MassActions/contexts/MassActions.context';
import EmptyState from '@components/v2/ui/EmptyState/EmptyState';
import OrdersIllustration from '@components/v2/ui/EmptyState/svg/OrdersIllustration';
import { useMemo } from 'react';
import useWidth from '../../../components/Hooks/useWidth';
import VirtualizedTable from '../../../components/ListV2/components/table/VirtualizedTable';
import { Column } from '../../../components/ListV2/components/table/VirtualizedTable.types';
import { LIST_PAGE_HEADER } from '../../../components/v2/layout/AppTemplate/appTemplate.consts';
import useHasActiveFilters from '../../../hooks/useHasActiveFilters';
import LangEnum from '../../../lang/LangEnum';
import { ColumnsPreferenceRecognizedLists } from '../../../services/modules/userContextPreferences/userContextPreferences.types';
import { UsedInOfflineMode } from '../../offline/types/UsedInOfflineMode';
import SummarizeRow from '../../reports/commons/SummarizeRow';
import useOrderRowActions from '../hooks/useOrderRowActions';
import { OrderListWithBaseItem } from '../order.types';
import { ORDERS_FILTERS_KEYS } from '../orderFilter/orderFilter.utils';
import OrderListParent from './OrderListParent';

const TAB_FILTER_KEY = 'tab';

interface OrderListProps extends UsedInOfflineMode {
  columns: Column<OrderListWithBaseItem>[];
  isAllItemsSelected: boolean;
  orders: OrderListWithBaseItem[];
  ordersSelected: string[];
  isLoadingMore: boolean;
  isLoadingTotals: boolean;

  loadMore: () => void;
  onToggleSelect: (key: string) => void;
  onToggleSelectAll: (field: string) => void;
  onToggleSelectParent: (
    parentItem: OrderListWithBaseItem,
    childItems: OrderListWithBaseItem[],
  ) => void;
  isLoadingPage: boolean;
  isArchivedOrderFilterActive?: boolean;
  restoreScrollOnMount?: boolean;
  customScrollParent?: HTMLElement | null;
  isCreateOrderHidden: boolean;
  onCreateOrder: () => void;
}

const OrderList = ({
  columns,
  orders,
  ordersSelected,
  isAllItemsSelected,
  loadMore,
  onToggleSelect,
  onToggleSelectAll,
  onToggleSelectParent,
  isLoadingMore,
  isLoadingTotals,
  isLoadingPage,
  isArchivedOrderFilterActive = false,
  restoreScrollOnMount = false,
  customScrollParent,
  isOfflineMode = false,
  isCreateOrderHidden,
  onCreateOrder,
}: OrderListProps) => {
  const { mobileWidth } = useWidth();
  const { massActionFilter } = useMassActionsContext();
  const { getRowActions, getIsDisabledRowAction } = useOrderRowActions({
    isArchivedOrderFilterActive,
    isOfflineMode,
  });

  const memoizedOrders = useMemo(() => orders, [orders]);

  const handleLoadMore = () => {
    if (!isLoadingMore) {
      loadMore();
    }
  };

  const hasActiveFilters = useHasActiveFilters({
    filterKeys: [...Object.values(ORDERS_FILTERS_KEYS), TAB_FILTER_KEY],
  });

  return (
    <VirtualizedTable<OrderListWithBaseItem>
      useCardView
      restoreScrollOnMount={restoreScrollOnMount}
      customScrollParent={customScrollParent}
      hasActiveFilters={hasActiveFilters}
      fixedTopOffset={
        customScrollParent
          ? 0
          : mobileWidth
            ? LIST_PAGE_HEADER.heightMobile
            : LIST_PAGE_HEADER.heightDesktop
      }
      columns={columns}
      isFirstLoading={isLoadingPage}
      isLoadingNextPage={isLoadingMore}
      columnsPreferenceRecognizedList={ColumnsPreferenceRecognizedLists.ORDERS}
      hideColumnsButton
      renderParent={({ item }) => <OrderListParent item={item} hasCheckBox />}
      getRowActions={getRowActions}
      data={memoizedOrders}
      endReached={handleLoadMore}
      getIsDisabledRowAction={getIsDisabledRowAction}
      renderTopItemList={(props) => (
        <SummarizeRow
          isUsingCheckbox
          className={props.className}
          visibleColumns={props.visibleColumns}
          isLoadingTotals={isLoadingTotals}
        />
      )}
      checkboxProps={{
        disableCheckBox: massActionFilter,
        selectedRows: ordersSelected,
        onToggleSelectAll: () => onToggleSelectAll('orderId'),
        isAllSelected: isAllItemsSelected,
        onToggleSelectRow: (item) => onToggleSelect(item.orderId!),
        onToggleSelectParent: onToggleSelectParent,
      }}
      emptyState={
        <EmptyState
          title={{ text: LangEnum.GLOBAL_ORDERS }}
          description={{ text: LangEnum.ORDER_LIST_EMPTY_DESCRIPTION }}
          illustration={<OrdersIllustration />}
          action={{
            primary: isCreateOrderHidden
              ? undefined
              : { label: LangEnum.GLOBAL_CREATE_ORDER, onClick: onCreateOrder },
          }}
        />
      }
    />
  );
};

export default OrderList;
